import { Check, ChevronDown } from 'lucide-react';
import {
  type CSSProperties,
  type KeyboardEvent,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

/** Visual/behavior props shared by Select and MultiSelect. */
interface SelectCommonProps {
  options: SelectOption[];
  /**
   * Typeahead filter. Defaults to `auto`: on once there are more than 5 options,
   * off below that. Pass `true`/`false` to force it either way.
   */
  searchable?: boolean;
  /**
   * Shown on the trigger when nothing is selected. When set, it also doubles as a
   * selectable empty row (value `''`) — the same role the native `<option value="">`
   * placeholder played: clears back to no value (single) / clears all (multi).
   */
  placeholder?: string;
  /** Placeholder for the typeahead input (searchable only). */
  searchPlaceholder?: string;
  id?: string;
  invalid?: boolean;
  disabled?: boolean;
  className?: string;
  style?: CSSProperties;
}

export interface SelectProps extends SelectCommonProps {
  value: string;
  onChange: (value: string) => void;
}

export interface MultiSelectProps extends SelectCommonProps {
  values: string[];
  onChange: (values: string[]) => void;
}

/** Auto-enable the typeahead filter once the list is long enough to be worth scanning. */
const SEARCHABLE_THRESHOLD = 5;
/** Upper bound for the content-sized menu so a long option label can't make it absurdly wide. */
const MENU_MAX_WIDTH = 360;

interface SelectBaseProps extends SelectCommonProps {
  multiple: boolean;
  /** Normalized selection ([''] = single with nothing chosen when a placeholder exists). */
  selected: string[];
  onSelect: (values: string[]) => void;
}

/**
 * Unified dropdown engine: a custom-rendered trigger + popover menu (not a native
 * `<select>`, so it can carry the design-system look). The menu is portalled to
 * `document.body` with fixed positioning so it never gets clipped by a scrolling
 * modal/drawer, and flips upward when there's no room below. Controlled; the
 * exported `Select` (single) and `MultiSelect` wrappers normalize the value shape.
 */
function SelectBase({
  multiple,
  selected,
  onSelect,
  options,
  searchable,
  placeholder,
  searchPlaceholder,
  id,
  invalid,
  disabled,
  className = '',
  style,
}: SelectBaseProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [menuPos, setMenuPos] = useState<CSSProperties | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const focusedRef = useRef(false);
  const reactId = useId();
  const baseId = id ?? reactId;

  const isSearchable = searchable ?? options.length > SEARCHABLE_THRESHOLD;

  // The placeholder is a real, selectable empty row (value '') so a value can be
  // cleared (single) / all values cleared (multi).
  const allOptions = useMemo<SelectOption[]>(
    () => (placeholder !== undefined ? [{ value: '', label: placeholder }, ...options] : options),
    [placeholder, options],
  );

  const q = query.trim().toLowerCase();
  const visible = useMemo(
    () => (isSearchable && q ? allOptions.filter((o) => o.label.toLowerCase().includes(q)) : allOptions),
    [isSearchable, q, allOptions],
  );

  const chosen = options.filter((o) => selected.includes(o.value));
  // Single mode mirrors the pre-refactor rule exactly: placeholder styling when
  // the value matches no option, or when it's the ''-placeholder row itself.
  const isPlaceholder = multiple
    ? chosen.length === 0
    : allOptions.find((o) => o.value === selected[0]) === undefined ||
      (selected[0] === '' && placeholder !== undefined);
  const triggerLabel = multiple
    ? chosen.length === 0
      ? (placeholder ?? '')
      : chosen.length === 1
        ? chosen[0]!.label
        : `${chosen[0]!.label} +${chosen.length - 1}`
    : (allOptions.find((o) => o.value === selected[0])?.label ?? placeholder ?? '');

  const isChecked = (o: SelectOption) =>
    multiple ? (o.value === '' ? chosen.length === 0 : selected.includes(o.value)) : o.value === selected[0];

  // Anchor the fixed-position menu to the trigger, flipping up when the space
  // below is too tight. Re-run on open and whenever the page (or a modal body)
  // scrolls or resizes under it.
  const place = useCallback(() => {
    const el = rootRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const gap = 4;
    const vh = window.innerHeight;
    const vw = window.innerWidth;
    const below = vh - r.bottom;
    const above = r.top;
    const openUp = below < 220 && above > below;
    setMenuPos({
      position: 'fixed',
      left: Math.round(r.left),
      // Grow to fit the longest option, but never narrower than the trigger nor
      // wider than a sensible cap / the room left to the viewport edge.
      width: 'max-content',
      minWidth: Math.round(r.width),
      maxWidth: Math.min(MENU_MAX_WIDTH, Math.round(vw - r.left - 8)),
      maxHeight: Math.max(140, Math.round((openUp ? above : below) - 8)),
      ...(openUp ? { bottom: Math.round(vh - r.top + gap) } : { top: Math.round(r.bottom + gap) }),
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setMenuPos(null);
      return;
    }
    place();
    const reposition = () => place();
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true); // capture: catch inner (modal) scroll too
    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [open, place]);

  // Close on outside click — must allow the portalled menu, which lives outside rootRef.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (rootRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // Move focus to the filter once the menu has mounted (searchable only). Guarded
  // so repositioning (scroll/resize) doesn't steal focus back on every tick.
  useEffect(() => {
    if (!open) {
      focusedRef.current = false;
      return;
    }
    if (isSearchable && menuPos && !focusedRef.current) {
      focusedRef.current = true;
      searchRef.current?.focus();
    }
  }, [open, isSearchable, menuPos]);

  // When opening: reset the query and point the active row at the current selection.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally runs only when `open` flips, snapshotting selection/options at that moment
  useEffect(() => {
    if (!open) return;
    setQuery('');
    const idx = visible.findIndex((o) => isChecked(o) && !o.disabled);
    setActive(idx >= 0 ? idx : visible.findIndex((o) => !o.disabled));
  }, [open]);

  // Keep the active row reachable when the filtered list shrinks/grows.
  useEffect(() => {
    if (open) setActive((a) => Math.min(Math.max(a, 0), Math.max(visible.length - 1, 0)));
  }, [visible.length, open]);

  // Scroll the active option into view as the user arrows through.
  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector<HTMLElement>(`[data-idx="${active}"]`);
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [active, open]);

  const commit = (opt: SelectOption | undefined) => {
    if (!opt || opt.disabled) return;
    if (!multiple) {
      onSelect([opt.value]);
      setOpen(false);
      return;
    }
    if (opt.value === '') {
      // The placeholder row is "clear all" in multi mode.
      onSelect([]);
      setOpen(false);
      return;
    }
    // Toggle and STAY OPEN so several values can be picked in one visit.
    onSelect(
      selected.includes(opt.value) ? selected.filter((v) => v !== opt.value) : [...selected, opt.value],
    );
  };

  const step = (dir: 1 | -1) => {
    setActive((a) => {
      const n = visible.length;
      if (n === 0) return a;
      let i = a;
      for (let k = 0; k < n; k++) {
        i = (i + dir + n) % n;
        if (!visible[i]?.disabled) return i;
      }
      return a;
    });
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (disabled) return;
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        step(1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        step(-1);
        break;
      case 'Home':
        e.preventDefault();
        setActive(visible.findIndex((o) => !o.disabled));
        break;
      case 'End':
        e.preventDefault();
        setActive(visible.map((o) => !o.disabled).lastIndexOf(true));
        break;
      case 'Enter':
        e.preventDefault();
        commit(visible[active]);
        break;
      case 'Tab':
      case 'Escape':
        setOpen(false);
        break;
    }
  };

  return (
    <div className={`cg-select-root ${className}`.trim()} ref={rootRef} style={style}>
      <button
        type="button"
        id={baseId}
        className="cg-select"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={`${baseId}-listbox`}
        aria-invalid={invalid || undefined}
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        onKeyDown={onKeyDown}
      >
        <span className={`cg-select__value${isPlaceholder ? ' cg-select__value--placeholder' : ''}`}>
          {triggerLabel}
        </span>
        <ChevronDown size={15} className="cg-select__caret" aria-hidden />
      </button>

      {open &&
        !disabled &&
        menuPos &&
        createPortal(
          <div ref={menuRef} className="cg-select__menu" style={menuPos}>
            {isSearchable && (
              <div className="cg-select__search">
                <input
                  ref={searchRef}
                  className="cg-input"
                  type="text"
                  autoComplete="off"
                  value={query}
                  placeholder={searchPlaceholder}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={onKeyDown}
                />
              </div>
            )}
            <div
              ref={listRef}
              className="cg-select__list"
              role="listbox"
              id={`${baseId}-listbox`}
              aria-multiselectable={multiple || undefined}
            >
              {visible.length === 0 ? (
                <div className="cg-select__empty">—</div>
              ) : (
                visible.map((o, i) => (
                  <button
                    key={o.value}
                    type="button"
                    role="option"
                    data-idx={i}
                    aria-selected={isChecked(o)}
                    aria-disabled={o.disabled || undefined}
                    className={`cg-select__option${i === active ? ' is-active' : ''}`}
                    disabled={o.disabled}
                    onMouseEnter={() => setActive(i)}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => commit(o)}
                  >
                    <span className="cg-select__option-label">{o.label}</span>
                    {isChecked(o) && <Check size={15} className="cg-select__check" aria-hidden />}
                  </button>
                ))
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}

/** Single-select, controlled, `onChange(value)` like every other value component. */
export function Select({ value, onChange, ...rest }: SelectProps) {
  return (
    <SelectBase {...rest} multiple={false} selected={[value]} onSelect={(vals) => onChange(vals[0] ?? '')} />
  );
}

/**
 * Multi-select variant of `Select`: same trigger/menu, but options toggle and the
 * menu stays open while picking; the placeholder row clears the whole selection.
 * Trigger shows the single chosen label, or "first +N" when several are chosen.
 */
export function MultiSelect({ values, onChange, ...rest }: MultiSelectProps) {
  return <SelectBase {...rest} multiple selected={values} onSelect={onChange} />;
}
